/**
 * Reviews — the requester's half of the review loop.
 *
 * Everything you sent out for review, internal and external, in one table.
 * The two lanes are different objects behind different endpoints (a `tasks`
 * notification; a share with approval enabled) but they answer the same
 * questions, so they share one set of columns and carry a type badge. The
 * flattening lives in `reviewRows.ts`; this file is presentation.
 *
 * Companion to Assigned to me, which is the same loop seen from the reviewer's
 * end. Splitting them is what lets a `user`-role reviewer land somewhere
 * without being shown a management surface they cannot use.
 *
 * See `docs/design/workflows-section.md`.
 */
import * as React from 'react';
import cn from 'classnames';
import moment from 'moment';
import { useHistory } from 'react-router-dom';

import { Button, Loader, PageBody, PageTitle } from 'src/components';
import { Checks } from 'src/common/icons';
import { TNotification } from 'src/types/TNotification';
import { Share } from 'src/types/TShare';
import { useAssignedReviewsQuery } from 'src/api/queries/notificationQueries';
import { useSharesListQuery } from 'src/api/queries/shareQueries';
import { useCloseReviewMutation } from 'src/api/mutations/shareMutations';
import { useResolveTaskMutation } from 'src/api/mutations/notificationMutations';
import { useAlerts } from 'src/common/AlertManager';
import useModal from 'src/hooks/useModal';
import { openNotificationUrl } from 'src/utils/notificationLink';
import SendForReviewModal from './SendForReviewModal';
import {
  ReviewRow,
  STATE_LABEL,
  byUrgency,
  fromShare,
  fromTask,
  isSettled,
  needsAttention,
  stateTone,
} from './reviewRows';

import styles from './styles.module.scss';

/**
 * Status, not lane. The lane is already on every row as a badge, and it was
 * never the question people open this page with — "what is outstanding",
 * "what needs me", "what is finished" are. Splitting by lane also meant the
 * default view grew forever, because a settled review had nowhere to go.
 */
type Tab = 'open' | 'attention' | 'done' | 'all';

const TAB_LABEL: Record<Tab, string> = {
  open: 'Open',
  attention: 'Needs attention',
  done: 'Done',
  all: 'All',
};

const TABS: Tab[] = ['open', 'attention', 'done', 'all'];

/** Progress meter. Splits the bar into decided-good and rejected so a row
 *  reading "88/142" also shows that 11 of them came back rejected. */
const Meter: React.FC<{ row: ReviewRow }> = ({ row }) => {
  if (!row.total) return <span className={styles.meterLabel}>—</span>;

  const approved = Math.max(0, row.decided - row.rejected);
  const pct = (n: number) => `${(n / row.total) * 100}%`;

  return (
    <span className={styles.meter}>
      <span className={styles.meterTrack} aria-hidden>
        {approved > 0 && <i className={styles.meterOk} style={{ width: pct(approved) }} />}
        {row.rejected > 0 && <i className={styles.meterBad} style={{ width: pct(row.rejected) }} />}
      </span>
      <span className={styles.meterLabel}>
        {row.lane === 'internal'
          ? STATE_LABEL[row.state]
          : `${row.decided}/${row.total}`}
      </span>
    </span>
  );
};

const Due: React.FC<{ dueOn: string | null; settled: boolean }> = ({ dueOn, settled }) => {
  if (!dueOn) return <span className={styles.due}>—</span>;

  const due = moment(dueOn);
  const past = due.isBefore(moment());
  const overdue = !settled && past;

  // A bare "4 days ago" in a Due column reads as "resolved 4 days ago". Say
  // "was due" once the deadline is history and the review is finished with.
  let text: string;
  if (overdue) {
    text = `overdue ${due.fromNow(true)}`;
  } else if (past) {
    text = `was due ${due.fromNow()}`;
  } else {
    text = due.fromNow();
  }

  return (
    <span
      className={cn(styles.due, { [styles.dueOverdue]: overdue })}
      title={due.format('LLL')}
    >
      {text}
    </span>
  );
};

export const Reviews: React.FC = () => {
  const history = useHistory();
  const { showModal } = useModal();
  const { showSuccess, showAlert } = useAlerts();
  const [tab, setTab] = React.useState<Tab>('open');

  const { data: assignedData, isLoading: loadingInternal } = useAssignedReviewsQuery();
  const { data: sharesData, isLoading: loadingExternal } = useSharesListQuery({
    limit: 100,
    offset: 0,
  });

  const rows = React.useMemo<ReviewRow[]>(() => {
    const tasks: TNotification[] = assignedData?.notifications ?? [];
    const shares: Share[] = sharesData?.data?.shares ?? [];

    return [
      ...tasks.map(fromTask),
      // Only shares that actually collect a verdict are reviews. The rest of
      // /shares is distribution — Web & Social, High-res, Print-ready — and
      // has no business on a list of reviews.
      ...shares.filter((s) => s.allow_approval).map(fromShare),
    ].sort(byUrgency);
  }, [assignedData, sharesData]);

  const counts = React.useMemo(
    () => ({
      all: rows.length,
      open: rows.filter((r) => !isSettled(r)).length,
      done: rows.filter((r) => isSettled(r)).length,
      attention: rows.filter((r) => needsAttention(r)).length,
    }),
    [rows],
  );

  const visible = React.useMemo(() => {
    switch (tab) {
      case 'open':
        return rows.filter((r) => !isSettled(r));
      case 'done':
        return rows.filter((r) => isSettled(r));
      case 'attention':
        return rows.filter((r) => needsAttention(r));
      default:
        return rows;
    }
  }, [rows, tab]);

  const isLoading = loadingInternal || loadingExternal;

  const closeReview = useCloseReviewMutation({
    onSuccess: () => showSuccess('Review closed'),
    onError: () => showAlert('Could not close that review'),
  });
  const withdrawReview = useResolveTaskMutation({
    onSuccess: () => showSuccess('Review withdrawn'),
    onError: () => showAlert('Could not withdraw that review'),
  });

  /** Stop waiting on a review that is never coming back. The verb differs by
   *  lane because the underlying objects do — withdrawing an assigned task
   *  tells the reviewer, closing a share just retires the link. */
  const retire = (event: React.MouseEvent, row: ReviewRow) => {
    // The row itself navigates; this button must not.
    event.stopPropagation();
    if (row.lane === 'internal') {
      withdrawReview.mutate({ id: Number(row.refId), action: 'cancel' });
    } else {
      closeReview.mutate(String(row.refId));
    }
  };

  const open = (row: ReviewRow) => {
    if (!row.href) return;
    openNotificationUrl(history, row.href);
  };

  return (
    <div className={styles.page} data-test="reviews-page-content">
      <PageTitle
        title="Reviews"
        counter={counts.all}
        className={styles.pageTitle}
        rightContainer={
          <Button size="small" onClick={() => showModal(<SendForReviewModal />)}>
            Start a review
          </Button>
        }
      />
      <PageBody>
        <p className={styles.pageSub}>
          Everything you’ve sent out for review, internal and external.
        </p>

        <div className={styles.tabs} role="tablist" aria-label="Filter reviews">
          {TABS.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              className={cn(styles.tab, { [styles.tabActive]: tab === value })}
              onClick={() => setTab(value)}
              data-test={`reviews-tab-${value}`}
            >
              <span>{TAB_LABEL[value]}</span>
              <span className={styles.tabCount}>{counts[value]}</span>
            </button>
          ))}
        </div>

        {isLoading && (
          <div className={styles.loading}>
            <Loader />
            <p>Loading reviews...</p>
          </div>
        )}

        {!isLoading && visible.length === 0 && (
          <div className={styles.empty} data-test="reviews-empty">
            <span className={styles.emptyIcon} aria-hidden>
              <Checks />
            </span>
            <p className={styles.emptyTitle}>
              {counts.all === 0 ? 'No reviews yet' : `Nothing under ${TAB_LABEL[tab]}`}
            </p>
            <p className={styles.emptyBody}>
              {counts.all === 0
                ? 'Send a collection to a teammate to check, or a review link to someone outside the account, and it appears here until it comes back.'
                : 'Try another tab to see the reviews you have in flight.'}
            </p>
            {counts.all === 0 && (
              <Button size="small" onClick={() => showModal(<SendForReviewModal />)}>
                Start a review
              </Button>
            )}
          </div>
        )}

        {!isLoading && visible.length > 0 && (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Review</th>
                  <th>Type</th>
                  <th>Reviewers</th>
                  <th>Progress</th>
                  <th>Due</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const settled = row.state !== 'in_review' && row.state !== 'not_opened';
                  return (
                    <tr
                      key={row.key}
                      className={cn({ [styles.rowHot]: needsAttention(row) })}
                      onClick={() => open(row)}
                      data-test="review-row"
                    >
                      <td>
                        <span className={styles.rowTitle}>{row.title}</span>
                        <span className={styles.rowDetail}>{row.detail}</span>
                      </td>
                      <td>
                        <span className={cn(styles.lane, styles[`lane_${row.lane}`])}>
                          {row.lane}
                        </span>
                      </td>
                      <td>
                        {row.reviewers.length > 0 ? (
                          <span className={styles.reviewer} title={row.reviewers.join(', ')}>
                            {row.reviewers[0]}
                          </span>
                        ) : (
                          <span className={styles.due}>—</span>
                        )}
                      </td>
                      <td>
                        <Meter row={row} />
                      </td>
                      <td>
                        <Due dueOn={row.dueOn} settled={settled} />
                      </td>
                      <td>
                        <span className={cn(styles.pill, styles[`pill_${stateTone(row.state)}`])}>
                          {STATE_LABEL[row.state]}
                        </span>
                      </td>
                      <td className={styles.rowActions}>
                        {/* Only a live review can be retired. A settled one
                            already has its answer. */}
                        {!settled && (
                          <button
                            type="button"
                            className={styles.rowAction}
                            onClick={(event) => retire(event, row)}
                            title={
                              row.lane === 'internal'
                                ? 'Withdraw this request — the reviewer is told'
                                : 'Close this review and stop chasing it'
                            }
                          >
                            {row.lane === 'internal' ? 'Withdraw' : 'Close'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </PageBody>
    </div>
  );
};

export default Reviews;
